import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/layout/Header";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatPrice, getImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Package, PackageOpen, Minus, Plus, Search, Truck, MapPin, Loader2 } from "lucide-react";

export default function CheckoutPage() {
  const { cart, fetchCart } = useCart();
  const { t, locale } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Buy Now state ──
  const buyNowState = location.state?.buy_now ? location.state : null;
  const isBuyNow = !!buyNowState;

  // ── PO Cart state ──
  const pOCartState = location.state?.po_cart ? location.state : null;
  const isPOCart = !!pOCartState;

  // Local qty state only used in buy_now mode
  const [buyNowQty, setBuyNowQty] = useState(buyNowState?.quantity || 1);

  const [notes, setNotes] = useState("");
  const [recipientName, setRecipientName] = useState(user?.name || "");
  const [recipientPhone, setRecipientPhone] = useState(user?.phone || "");
  const [recipientAddress, setRecipientAddress] = useState(user?.address || "");
  const [recipientPostalCode, setRecipientPostalCode] = useState(user?.postal_code || "");
  const [addressBook, setAddressBook] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [destinationQuery, setDestinationQuery] = useState("");
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [courierCode, setCourierCode] = useState("jne");
  const [shippingQuotes, setShippingQuotes] = useState([]);
  const [selectedShippingQuote, setSelectedShippingQuote] = useState(null);
  const [shippingSearching, setShippingSearching] = useState(false);
  const [shippingCalculating, setShippingCalculating] = useState(false);
  const [shippingError, setShippingError] = useState("");
  const [autoDestinationSearch, setAutoDestinationSearch] = useState(false);

  // ── Normal cart mode ──
  const cartItems = cart.items || [];
  const getCartItemPricing = (item) => item?.pricing || item?.product?.pricing || { final_price: item?.product?.price || 0, base_price: item?.product?.price || 0, savings: 0, size_price_adjustment: 0 };
  const cartTotal = cartItems.reduce((acc, i) => acc + (getCartItemPricing(i).final_price || i.product?.price || 0) * i.quantity, 0);
  const cartSavings = cartItems.reduce((acc, i) => acc + (getCartItemPricing(i).savings || 0) * i.quantity, 0);

  const hasReadyStock = useMemo(() => cartItems.some(i => i.product?.stock_type !== "preorder"), [cartItems]);
  const hasPreorder = useMemo(() => cartItems.some(i => i.product?.stock_type === "preorder"), [cartItems]);
  const isMixed = hasReadyStock && hasPreorder;
  const checkoutHasReadyStock = isPOCart ? false : (isBuyNow ? buyNowState?.stock_type !== "preorder" : hasReadyStock);

  // ── Buy Now computed values ──
  const buyNowTotal = isBuyNow ? (buyNowState.product_price * buyNowQty) : 0;
  const buyNowSavings = isBuyNow ? ((buyNowState.savings_per_item || 0) * buyNowQty) : 0;

  // ── PO Cart computed values ──
  const poCartItems = pOCartState?.items || [];
  const poCartTotal = useMemo(() =>
    poCartItems.reduce((acc, i) => acc + (i.price || pOCartState?.product_price || 0) * i.quantity, 0),
    [poCartItems, pOCartState]
  );
  const poCartSavings = useMemo(() =>
    poCartItems.reduce((acc, i) => acc + (i.savings || pOCartState?.savings_per_unit || 0) * i.quantity, 0),
    [poCartItems, pOCartState]
  );

  const courierOptions = useMemo(() => {
    const raw = (tenant?.shipping_couriers || "").split(/[,:\s]+/).map((item) => item.trim().toLowerCase()).filter(Boolean);
    return raw.length ? raw : ["jne", "sicepat", "jnt"];
  }, [tenant?.shipping_couriers]);

  const resolveVariantWeight = (product, variant) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const normalizedVariant = variant && typeof variant === "object" ? variant : {};
    const matchedVariant = variants.find((item) => {
      if (!item || typeof item !== "object") return false;
      return (!normalizedVariant.style || item.style === normalizedVariant.style)
        && (!normalizedVariant.color || item.color === normalizedVariant.color)
        && (!normalizedVariant.size || item.size === normalizedVariant.size);
    });

    const variantWeight = matchedVariant?.weight_gram;
    if (variantWeight !== undefined && variantWeight !== null && variantWeight !== "") {
      const parsed = Number(variantWeight);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }

    const productWeight = product?.weight_gram ?? product?.weight ?? product?.berat ?? product?.weight_g;
    const parsedProductWeight = Number(productWeight);
    if (Number.isFinite(parsedProductWeight) && parsedProductWeight > 0) return parsedProductWeight;

    return 100;
  };

  const shippingWeight = useMemo(() => {
    if (isPOCart) {
      return poCartItems.reduce((acc, item) => {
        const weight = resolveVariantWeight(pOCartState, item.variant);
        return acc + (Number(item.quantity || 1) * weight);
      }, 0);
    }
    if (isBuyNow) {
      const weight = resolveVariantWeight(buyNowState, buyNowState?.variant);
      return Number(buyNowQty || 1) * weight;
    }
    return cartItems.reduce((acc, item) => {
      const weight = resolveVariantWeight(item.product, item.variant);
      return acc + (Number(item.quantity || 1) * weight);
    }, 0);
  }, [isPOCart, isBuyNow, poCartItems, pOCartState, buyNowQty, buyNowState, cartItems]);

  const itemsSubtotal = isPOCart ? poCartTotal : (isBuyNow ? buyNowTotal : cartTotal);
  const totalSavings = isPOCart ? poCartSavings : (isBuyNow ? buyNowSavings : cartSavings);
  const hasShippingOrigin = !!(tenant?.shipping_origin_subdistrict_id || tenant?.shipping_origin_district_id || tenant?.shipping_origin_city_id || tenant?.shipping_origin_province_id);
  // Public /api/tenant intentionally masks the actual shipping_api_key, so storefront
  // readiness can only trust configured origin and let shipping endpoints validate the key.
  const shippingSettingsReady = hasShippingOrigin;
  const selectedShippingCost = checkoutHasReadyStock && shippingSettingsReady ? Number(selectedShippingQuote?.shipping_cost || 0) : 0;
  const grandTotal = itemsSubtotal + selectedShippingCost;
  const readyStockShippingReady = !checkoutHasReadyStock || (
    recipientName.trim() &&
    recipientPhone.trim() &&
    recipientAddress.trim() &&
    (
      !shippingSettingsReady ||
      (selectedDestination?.id && selectedShippingQuote && selectedShippingCost > 0)
    )
  );

  useEffect(() => {
    let alive = true;
    API.get("/tenant")
      .then((r) => {
        if (!alive) return;
        const next = r.data || {};
        setTenant(next);
        const courierList = (next.shipping_couriers || "").split(/[,:\s]+/).map((item) => item.trim().toLowerCase()).filter(Boolean);
        if (courierList.length) setCourierCode((prev) => courierList.includes(prev) ? prev : courierList[0]);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!user) return;
    API.get("/address-book").then((res) => {
      const addresses = res.data.addresses || [];
      setAddressBook(addresses);
      const defaultAddress = addresses.find((item) => item.is_default) || addresses[0];
      if (defaultAddress && !selectedAddressId) {
        setSelectedAddressId(defaultAddress.id);
        setRecipientName(defaultAddress.recipient_name || user?.name || "");
        setRecipientPhone(defaultAddress.recipient_phone || user?.phone || "");
        setRecipientAddress(defaultAddress.address || user?.address || "");
        setRecipientPostalCode(defaultAddress.postal_code || user?.postal_code || "");
        if (defaultAddress.destination_id) {
          setSelectedDestination({ ...(defaultAddress.destination_snapshot || {}), id: defaultAddress.destination_id, label: defaultAddress.destination_label });
          setDestinationQuery(defaultAddress.destination_label || "");
        }
      }
    }).catch(() => {});
  }, [user]);

  const clearShippingSelection = () => {
    setDestinationQuery("");
    setDestinationOptions([]);
    setSelectedDestination(null);
    setShippingQuotes([]);
    setSelectedShippingQuote(null);
    setShippingError("");
    setAutoDestinationSearch(false);
  };

  const applyAddressBookItem = (addressId) => {
    setSelectedAddressId(addressId);
    const selected = addressBook.find((item) => item.id === addressId);
    if (!selected) {
      setRecipientName("");
      setRecipientPhone("");
      setRecipientAddress("");
      setRecipientPostalCode("");
      clearShippingSelection();
      return;
    }
    const postalCode = selected.postal_code || "";
    const destination = selected.destination_id
      ? { ...(selected.destination_snapshot || {}), id: selected.destination_id, label: selected.destination_label }
      : null;
    const searchQuery = selected.destination_label || destination?.label || postalCode || "";

    setRecipientName(selected.recipient_name || "");
    setRecipientPhone(selected.recipient_phone || "");
    setRecipientAddress(selected.address || "");
    setRecipientPostalCode(postalCode);
    setDestinationOptions([]);
    setShippingQuotes([]);
    setSelectedShippingQuote(null);
    setSelectedDestination(destination);
    setDestinationQuery(searchQuery);
    setShippingError(destination ? "" : (postalCode ? "Mencari area RajaOngkir dari kode pos..." : "Alamat ini belum punya area RajaOngkir. Cari tujuan manual untuk hitung ongkir."));
    setAutoDestinationSearch(!destination && postalCode.trim().length >= 3);
  };

  useEffect(() => {
    if (selectedDestination) {
      const selectedLabel = (selectedDestination.label || selectedDestination.name || selectedDestination.city_name || selectedDestination.district_name || selectedDestination.subdistrict_name || "").trim();
      if (destinationQuery.trim() && destinationQuery.trim() !== selectedLabel) {
        setSelectedDestination(null);
        setShippingQuotes([]);
        setSelectedShippingQuote(null);
      }
    }
  }, [destinationQuery, selectedDestination]);

  useEffect(() => {
    let alive = true;
    const query = destinationQuery.trim();
    if (query.length < 3) {
      setDestinationOptions([]);
      return () => { alive = false; };
    }
    setShippingSearching(true);
    const timer = setTimeout(async () => {
      try {
        const r = await API.get("/shipping/destinations", { params: { search: query, limit: 10, offset: 0 } });
        if (!alive) return;
        const destinations = r.data?.data || [];
        if (autoDestinationSearch && destinations.length === 1) {
          const dest = destinations[0];
          const label = dest.label || dest.name || [dest.subdistrict_name, dest.district_name, dest.city_name, dest.province_name].filter(Boolean).join(", ") || query;
          setSelectedDestination(dest);
          setDestinationQuery(label);
          setDestinationOptions([]);
          setShippingError("Area RajaOngkir dipilih otomatis dari kode pos.");
          setAutoDestinationSearch(false);
        } else {
          setDestinationOptions(destinations);
          if (autoDestinationSearch) {
            setShippingError(destinations.length
              ? "Pilih area RajaOngkir yang paling sesuai dari hasil pencarian kode pos."
              : "Area RajaOngkir belum ditemukan dari kode pos ini. Coba cari manual dengan kelurahan/kecamatan.");
          }
        }
      } catch {
        if (alive) {
          setDestinationOptions([]);
          if (autoDestinationSearch) setShippingError("Gagal mencari area RajaOngkir dari kode pos. Coba cari manual dengan kelurahan/kecamatan.");
        }
      } finally {
        if (alive) setShippingSearching(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [destinationQuery, autoDestinationSearch]);

  useEffect(() => {
    let alive = true;
    const destinationId = selectedDestination?.id;
    if (!shippingSettingsReady || !destinationId || !courierCode) {
      setShippingQuotes([]);
      setSelectedShippingQuote(null);
      setShippingError("");
      return () => { alive = false; };
    }
    setShippingCalculating(true);
    setShippingError("");
    const timer = setTimeout(async () => {
      try {
        const origin = tenant?.shipping_origin_subdistrict_id || tenant?.shipping_origin_district_id || tenant?.shipping_origin_city_id || tenant?.shipping_origin_province_id || "";
        const r = await API.post("/shipping/calculate", {
          origin,
          destination: String(destinationId),
          weight: Number(shippingWeight) || 100,
          courier: courierCode,
          price: "lowest",
        });
        if (!alive) return;
        const raw = r.data;
        const list = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
        const normalized = list
          .map((item, idx) => {
            const serviceCode = item.code || item.service_code || item.service || item.name || `svc-${idx}`;
            const serviceName = item.service || item.name || item.service_code || serviceCode;
            const shippingCostValue = Number(item.cost ?? item.price ?? item.value ?? 0) || 0;
            return {
              id: `${courierCode}-${serviceCode}-${idx}`,
              courier_code: courierCode,
              courier_name: courierCode.toUpperCase(),
              service_code: serviceCode,
              service_name: serviceName,
              shipping_cost: shippingCostValue,
              shipping_etd: item.etd || "",
              shipping_description: item.description || "",
              raw: item,
            };
          })
          .filter((item) => item.service_name);
        setShippingQuotes(normalized);
        setSelectedShippingQuote((prev) => {
          if (prev) {
            const found = normalized.find((item) => item.service_code === prev.service_code && item.shipping_cost === prev.shipping_cost);
            if (found) return found;
          }
          return normalized[0] || null;
        });
      } catch (err) {
        if (!alive) return;
        setShippingQuotes([]);
        setSelectedShippingQuote(null);
        setShippingError(err.response?.data?.detail || "Failed to calculate shipping");
      } finally {
        if (alive) setShippingCalculating(false);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [selectedDestination?.id, courierCode, shippingWeight, shippingSettingsReady, tenant?.shipping_origin_subdistrict_id, tenant?.shipping_origin_district_id, tenant?.shipping_origin_city_id, tenant?.shipping_origin_province_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const cleanRecipientName = (checkoutHasReadyStock ? recipientName : (user?.name || "")).trim();
    const cleanRecipientPhone = (checkoutHasReadyStock ? recipientPhone : (user?.phone || "")).trim();
    const cleanRecipientAddress = (checkoutHasReadyStock ? recipientAddress : (user?.address || "")).trim();
    const shippingPayload = checkoutHasReadyStock ? {
      shipping_cost: selectedShippingCost,
      postal_code: recipientPostalCode.trim(),
      shipping_weight_grams: Number(shippingWeight) || 100,
      destination_id: shippingSettingsReady ? String(selectedDestination?.id || "") : "",
      destination_label: shippingSettingsReady ? (selectedDestination?.label || selectedDestination?.name || destinationQuery) : "",
      destination_snapshot: shippingSettingsReady ? (selectedDestination || {}) : {},
      courier: shippingSettingsReady ? (selectedShippingQuote?.courier_code || courierCode) : "",
      shipping_service: shippingSettingsReady ? (selectedShippingQuote?.service_name || selectedShippingQuote?.service_code || "") : "",
      shipping_etd: shippingSettingsReady ? (selectedShippingQuote?.shipping_etd || "") : "",
      shipping_quote_snapshot: shippingSettingsReady ? (selectedShippingQuote?.raw || selectedShippingQuote || {}) : {},
      shipping_pending_setup: !shippingSettingsReady,
    } : {};

    const payloadInfo = {
      name: cleanRecipientName,
      phone: cleanRecipientPhone,
      address: cleanRecipientAddress,
      address_book_id: selectedAddressId,
      notes,
      payment_method: "bank_transfer",
      shipping: shippingPayload,
    };

    try {
      let data;
      if (isPOCart) {
        const res = await API.post("/orders/po-cart", {
          product_id: pOCartState.product_id,
          items: poCartItems,
          ...payloadInfo
        });
        data = res.data;
        sessionStorage.removeItem(`po_cart_${pOCartState.product_id}`);
        toast.success((locale === "id" ? "Pesanan PO berhasil dibuat! ID: " : "PO Order placed! ID: ") + data.id);
      } else if (isBuyNow) {
        const res = await API.post("/orders/instant", {
          product_id: buyNowState.product_id,
          variant: buyNowState.variant || {},
          quantity: buyNowQty,
          ...payloadInfo
        });
        data = res.data;
        toast.success(t("checkout.success_msg").replace("{id}", data.id));
      } else {
        const res = await API.post("/orders", payloadInfo);
        data = res.data;
        if (data.split) {
          const rsTrx = data.transactions.find(t => t.status === "pending");
          const poTrx = data.transactions.find(t => t.status === "preorder");
          toast.success(
            t("checkout.split_success")
              .replace("{rs}", rsTrx?.id || "")
              .replace("{po}", poTrx?.id || ""),
            { duration: 6000 }
          );
        } else {
          toast.success(t("checkout.success_msg").replace("{id}", data.id));
        }
        await fetchCart();
      }
      if (data.split) {
        navigate("/orders");
      } else {
        navigate(`/orders/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-stone-50 pb-20" data-testid="checkout-page">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24 lg:pt-32">
        <h1 className="font-serif text-3xl text-stone-900 mb-8 text-center">{t("checkout.title")}</h1>

        {/* Split Order Notice (cart mode only) */}
        {!isBuyNow && isMixed && (
          <div className="mb-6 flex gap-3 items-start bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-1">{locale === "id" ? "Checkout perlu dipisah" : "Checkout must be separated"}</p>
              <p className="text-sm text-amber-700">{locale === "id" ? "Ready Stock wajib alamat + ongkir sebelum checkout, sedangkan PO memakai flow DP terpisah. Checkout Ready Stock dan PO satu per satu." : "Ready Stock requires address + shipping cost before checkout, while PO uses a separate DP flow. Please checkout Ready Stock and PO separately."}</p>
              <div className="flex gap-4 mt-3">
                <span className="flex items-center gap-1.5 text-xs text-amber-700 font-medium bg-white px-2 py-1 rounded">
                  <Package className="w-3.5 h-3.5" /> Ready Stock → <span className="font-bold">Pending</span>
                </span>
                <span className="flex items-center gap-1.5 text-xs text-amber-700 font-medium bg-white px-2 py-1 rounded">
                  <PackageOpen className="w-3.5 h-3.5" /> Pre-Order → <span className="font-bold">Pre-Order</span>
                </span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Order Summary Block */}
          <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-serif text-lg text-stone-900 mb-4 pb-4 border-b border-stone-100">{t("checkout.summary")}</h2>

            {/* ─── BUY NOW MODE: single product with qty editor ─── */}
            {isPOCart ? (
              /* ─── PO CART MODE: multi-variant summary ─── */
              <div className="space-y-3 mb-4">
                {/* Product header */}
                <div className="flex gap-3 pb-3 border-b border-stone-100">
                  <div className="w-12 h-14 bg-stone-100 rounded overflow-hidden shrink-0">
                    <img src={pOCartState.product_image ? getImageUrl(pOCartState.product_image) : ""} alt={pOCartState.product_name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <PackageOpen className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Pre-Order</p>
                    </div>
                    <p className="text-sm font-semibold text-stone-800 line-clamp-2">{pOCartState.product_name} by {pOCartState.brand_name || pOCartState.brand?.name || pOCartState.brand_id?.replace("brand_", "")}</p>
                    {pOCartState.preorder_closing_date && (
                      <p className="text-[11px] text-amber-600 mt-0.5">Closing: {pOCartState.preorder_closing_date}</p>
                    )}
                  </div>
                </div>
                {/* Per-variant rows */}
                {poCartItems.map((item, idx) => {
                  const variantLabel = [item.variant?.style, item.variant?.size, item.variant?.color].filter(Boolean).join(" · ") || (locale === "id" ? "Tanpa varian" : "No variant");
                  const itemPrice = item.price || pOCartState.product_price || 0;
                  const itemSavings = Number(item.savings || pOCartState?.savings_per_unit || 0);
                  const originalItemPrice = itemPrice + itemSavings;
                  const rowTotal = item.quantity * itemPrice;
                  const originalRowTotal = item.quantity * originalItemPrice;
                  return (
                    <div key={idx} className="flex justify-between text-sm gap-2 border-b border-stone-50 pb-2">
                      <div className="flex flex-col">
                        <span className="text-stone-600 truncate font-medium">{variantLabel}</span>
                        <span className="text-xs text-stone-400">
                          {itemSavings > 0 && <span className="line-through mr-1.5">{formatPrice(originalItemPrice)}</span>}
                          <span>{formatPrice(itemPrice)} × {item.quantity}</span>
                          {itemSavings > 0 && originalItemPrice > 0 && (
                            <span className="ml-1 text-red-400">(-{Math.round((itemSavings / originalItemPrice) * 100)}%)</span>
                          )}
                        </span>
                        {Number(item.variant?.size_price_adjustment || 0) > 0 && (
                          <span className="text-xs text-stone-400">Tambahan size: +{formatPrice(item.variant.size_price_adjustment)}</span>
                        )}
                      </div>
                      <span className="text-stone-900 font-medium whitespace-nowrap">
                        {itemSavings > 0 && <span className="block text-[11px] text-stone-400 line-through">{formatPrice(originalRowTotal)}</span>}
                        {formatPrice(rowTotal)}
                      </span>
                      </div>
                  );
                })}
              </div>
            ) : isBuyNow ? (
              <div className="space-y-4 mb-4">
                <div className="flex gap-3">
                  <div className="w-16 h-20 bg-stone-100 rounded overflow-hidden shrink-0">
                    <img src={buyNowState.product_image ? getImageUrl(buyNowState.product_image) : ""} alt={buyNowState.product_name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-1 mb-1">
                      <p className="text-sm font-medium text-stone-800 leading-snug line-clamp-2">{buyNowState.product_name} by {buyNowState.brand_name || buyNowState.brand?.name || buyNowState.brand_id?.replace("brand_", "")}</p>
                      {buyNowState.stock_type === "preorder"
                        ? <PackageOpen className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                        : <Package className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      }
                    </div>
                    {(buyNowState.variant?.size || buyNowState.variant?.color) && (
                      <p className="text-xs text-stone-400 mb-2">
                        {buyNowState.variant.size}
                        {buyNowState.variant.size && buyNowState.variant.color && " / "}
                        {buyNowState.variant.color}
                      </p>
                    )}
                    <p className="text-xs text-stone-500 mb-1">
                      {Number(buyNowState.savings_per_item || 0) > 0 && (
                        <span className="text-stone-400 line-through mr-1.5">{formatPrice(Number(buyNowState.product_price || 0) + Number(buyNowState.savings_per_item || 0))}</span>
                      )}
                      <span>{formatPrice(buyNowState.product_price)} / pcs</span>
                      {Number(buyNowState.savings_per_item || 0) > 0 && (Number(buyNowState.product_price || 0) + Number(buyNowState.savings_per_item || 0)) > 0 && (
                        <span className="ml-1 text-red-400">(-{Math.round((Number(buyNowState.savings_per_item || 0) / (Number(buyNowState.product_price || 0) + Number(buyNowState.savings_per_item || 0))) * 100)}%)</span>
                      )}
                    </p>
                    {Number(buyNowState.variant?.size_price_adjustment || 0) > 0 && (
                      <p className="text-xs text-stone-400 mb-3">Tambahan size: +{formatPrice(buyNowState.variant.size_price_adjustment)}</p>
                    )}
                    {/* Qty editor */}
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setBuyNowQty(q => Math.max(1, q - 1))} className="w-7 h-7 border border-stone-200 rounded flex items-center justify-center hover:bg-stone-50 transition-colors">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-sm font-bold text-stone-800 w-6 text-center">{buyNowQty}</span>
                      <button type="button" onClick={() => setBuyNowQty(q => q + 1)} className="w-7 h-7 border border-stone-200 rounded flex items-center justify-center hover:bg-stone-50 transition-colors">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* ─── NORMAL CART MODE: read-only list ─── */
              <div className="space-y-3 mb-4">
                {cartItems.map((item) => {
                  const itemPricing = getCartItemPricing(item);
                  return (
                    <div key={item.item_id} className="space-y-1">
                      <div className="flex justify-between text-sm gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {item.product?.stock_type === "preorder"
                            ? <PackageOpen className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                            : <Package className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          }
                          <span className="text-stone-600 truncate">{item.product?.name} by {item.product?.brand_name || item.product?.brand?.name || item.product?.brand_id?.replace("brand_", "")} x{item.quantity}</span>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          {(itemPricing.savings || 0) > 0 && (
                            <span className="text-xs text-stone-400 line-through mr-1.5">{formatPrice(((itemPricing.final_price || item.product?.price || 0) + (itemPricing.savings || 0)) * item.quantity)}</span>
                          )}
                          <span className="text-stone-900 font-medium">
                            {formatPrice((itemPricing.final_price || item.product?.price || 0) * item.quantity)}
                          </span>
                          {(itemPricing.savings || 0) > 0 && ((itemPricing.final_price || item.product?.price || 0) + (itemPricing.savings || 0)) > 0 && (
                            <span className="text-xs text-red-400 ml-1">(-{Math.round(((itemPricing.savings || 0) / ((itemPricing.final_price || item.product?.price || 0) + (itemPricing.savings || 0))) * 100)}%)</span>
                          )}
                        </div>
                      </div>
                      {Number(item.variant?.size_price_adjustment || itemPricing.size_price_adjustment || 0) > 0 && (
                        <p className="text-[11px] text-stone-400 ml-[24px]">Tambahan size: +{formatPrice(item.variant?.size_price_adjustment || itemPricing.size_price_adjustment)}</p>
                      )}
                      {item.item_notes && (
                        <p className="text-[11px] text-stone-400 italic ml-[24px]">📝 {item.item_notes}</p>
                      )}
                    </div>
                  );
                })}
                {!isBuyNow && isMixed && (
                  <div className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {locale === "id" ? "Akan dibagi 2 transaksi" : "Will be split into 2 transactions"}
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-stone-100 pt-4 mt-6 space-y-2">
              {/* Total harga normal (sebelum diskon) */}
              {totalSavings > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-stone-500">{locale === "id" ? "Total Harga Normal" : "Total Normal Price"}</span>
                  <span className="text-stone-400 line-through">{formatPrice(itemsSubtotal + totalSavings)}</span>
                </div>
              )}
              {/* Total diskon */}
              {totalSavings > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-stone-500">{locale === "id" ? "Total Diskon" : "Total Discount"}</span>
                  <span className="text-red-500 font-medium">
                    -{formatPrice(totalSavings)}
                    {(() => {
                      const normalTotal = itemsSubtotal + totalSavings;
                      const discPct = normalTotal > 0 ? Math.round((totalSavings / normalTotal) * 100) : 0;
                      return <span className="text-xs ml-1 text-red-400">(-{discPct}%)</span>;
                    })()}
                  </span>
                </div>
              )}
              {checkoutHasReadyStock && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-stone-500">{locale === "id" ? "Ongkir" : "Shipping"}</span>
                  <span className="text-stone-700 font-medium">{selectedShippingCost > 0 ? formatPrice(selectedShippingCost) : "-"}</span>
                </div>
              )}
              {/* Pembayaran */}
              <div className="flex justify-between items-center pt-2 border-t border-stone-100">
                <span className="font-semibold text-stone-700">{locale === "id" ? "Total Pembayaran" : "Total Payment"}</span>
                <span className="text-xl font-serif font-bold text-primary" data-testid="checkout-total">
                  {formatPrice(grandTotal)}
                </span>
              </div>
              {totalSavings > 0 && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                  <span className="text-emerald-600 text-sm">🎉</span>
                  <span className="text-sm font-medium text-emerald-700">
                    {locale === "id" ? "Kamu hemat" : "You save"} {formatPrice(totalSavings)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {checkoutHasReadyStock && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <Truck className="w-5 h-5 text-primary mt-1" />
                <div>
                  <h2 className="font-serif text-lg text-stone-900">{locale === "id" ? "Alamat & Ongkir" : "Address & Shipping"}</h2>
                  <p className="text-xs text-stone-500">{locale === "id" ? "Ready Stock wajib isi alamat dan pilih ongkir sebelum checkout." : "Ready Stock requires address and selected shipping cost before checkout."}</p>
                </div>
              </div>
              <div className="space-y-4">
                {addressBook.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Buku Alamat</label>
                    <select value={selectedAddressId} onChange={(e) => applyAddressBookItem(e.target.value)} className="w-full px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-primary focus:bg-white">
                      <option value="">Isi Alamat Tujuan Pengiriman</option>
                      {addressBook.map((address) => (
                        <option key={address.id} value={address.id}>{address.label || "Alamat"} — {address.recipient_name} {address.is_default ? "(Default)" : ""}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-stone-500">Data penerima, alamat, kode pos, dan tujuan RajaOngkir akan terisi jika tersedia. Jika belum ada tujuan RajaOngkir, sistem otomatis mencari dari kode pos.</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-stone-500">{locale === "id" ? "Nama Penerima" : "Recipient Name"}</label>
                    <input value={recipientName} onChange={(e) => { setRecipientName(e.target.value); setSelectedAddressId(""); }} placeholder={locale === "id" ? "Nama penerima" : "Recipient name"} className="w-full px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-primary focus:bg-white" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-stone-500">{locale === "id" ? "Nomor HP / WhatsApp" : "Phone / WhatsApp"}</label>
                    <input value={recipientPhone} onChange={(e) => { setRecipientPhone(e.target.value); setSelectedAddressId(""); }} placeholder={locale === "id" ? "Nomor HP / WhatsApp" : "Phone / WhatsApp"} className="w-full px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-primary focus:bg-white" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">{locale === "id" ? "Alamat Lengkap" : "Full Address"}</label>
                  <textarea value={recipientAddress} onChange={(e) => { setRecipientAddress(e.target.value); setSelectedAddressId(""); }} rows={3} placeholder={locale === "id" ? "Alamat lengkap penerima" : "Full recipient address"} className="w-full px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-primary focus:bg-white" />
                </div>
                {!shippingSettingsReady ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Ongkir otomatis belum aktif karena origin shipping belum diset di Shipping Settings. Order tetap bisa dibuat, ongkir akan diisi admin saat proses pengiriman.
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{locale === "id" ? "Cek Ongkir" : "Check Shipping Cost"}</label>
                        <p className="text-[11px] text-stone-500 mt-1">{locale === "id" ? "Cari tujuan berdasarkan kecamatan/kota atau kode pos." : "Search destination by district/city or postal code."}</p>
                      </div>
                      <div className="relative">
                        <Search className="w-4 h-4 text-stone-400 absolute left-3 top-3.5" />
                        <input value={destinationQuery} onChange={(e) => { setDestinationQuery(e.target.value); setRecipientPostalCode(e.target.value); setSelectedAddressId(""); setAutoDestinationSearch(false); }} placeholder={locale === "id" ? "Cari kecamatan/kota/kode pos tujuan" : "Search district/city/postal code"} className="w-full pl-10 pr-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-primary focus:bg-white" />
                        {shippingSearching && <Loader2 className="w-4 h-4 text-stone-400 animate-spin absolute right-3 top-3.5" />}
                      </div>
                      {destinationOptions.length > 0 && !selectedDestination && (
                        <div className="border border-stone-200 rounded-lg divide-y divide-stone-100 bg-white overflow-hidden">
                          {destinationOptions.map((dest) => {
                            const label = dest.label || dest.name || [dest.subdistrict_name, dest.district_name, dest.city_name, dest.province_name].filter(Boolean).join(", ");
                            return (
                              <button key={dest.id || label} type="button" onClick={() => { setSelectedDestination(dest); setDestinationQuery(label); setAutoDestinationSearch(false); setShippingError(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50">
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <select value={courierCode} onChange={(e) => setCourierCode(e.target.value)} className="px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-primary focus:bg-white">
                        {courierOptions.map((code) => <option key={code} value={code}>{code.toUpperCase()}</option>)}
                      </select>
                      <div className="px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg text-stone-600">
                        {locale === "id" ? "Berat" : "Weight"}: {Math.max(1, Number(shippingWeight) || 100)} gr
                      </div>
                    </div>
                    {shippingCalculating && <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{locale === "id" ? "Menghitung ongkir..." : "Calculating shipping..."}</div>}
                    {shippingError && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{shippingError}</div>}
                    {shippingQuotes.length > 0 && (
                      <div className="grid gap-2">
                        {shippingQuotes.map((quote) => (
                          <button key={quote.id} type="button" onClick={() => setSelectedShippingQuote(quote)} className={`text-left rounded-lg border px-3 py-3 transition-colors ${selectedShippingQuote?.id === quote.id ? "border-primary bg-primary/5" : "border-stone-200 bg-white hover:bg-stone-50"}`}>
                            <div className="flex justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-stone-800">{quote.courier_name} {quote.service_name}</p>
                                {quote.shipping_etd && <p className="text-xs text-stone-500">ETD: {quote.shipping_etd}</p>}
                              </div>
                              <p className="text-sm font-bold text-primary whitespace-nowrap">{formatPrice(quote.shipping_cost)}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-serif text-lg text-stone-900 mb-4">{locale === "id" ? "Catatan Pesanan" : "Order Notes"}</h2>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder={locale === "id" ? "Contoh: tolong packing rapi, warna jangan tertukar, dll." : "e.g. please pack carefully, don't mix colors, etc."}
              className="w-full px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-primary focus:bg-white transition-colors placeholder:text-stone-400"
              data-testid="checkout-notes"
            />
          </div>

          <button type="submit" disabled={loading || isMixed || !readyStockShippingReady || (isPOCart ? poCartItems.length === 0 : !isBuyNow && cartItems.length === 0)}
            className="w-full bg-primary text-primary-foreground py-4 rounded-xl font-semibold uppercase tracking-wider hover:opacity-90 transition-all disabled:opacity-50 shadow-sm border border-transparent hover:shadow-md"
            data-testid="place-order-btn"
          >
            {loading ? t("checkout.placing_order") : t("checkout.place_order")}
          </button>
        </form>
      </div>
    </div>
  );
}
